import { PAGE_ROUTES, SOFTPHONE_PAGE_ROUTES } from '@cloud-wave/neon-common-lib/app';

import TCustomerProfile from 'lib/common/contexts/ContactContext/types/CustomerProfile';

import MessageStatus from 'lib/common/constants/chat/MessageStatus';
import ParticipantRole from 'lib/common/constants/chat/ParticipantRole';
import CHAT_EVENTS from 'lib/common/constants/chat/chatEvents';

import { TFailedChat } from 'lib/common/types/chat/FailedChat';
import connectGetter from 'lib/common/utils/connectGetter';
import getUserName from 'lib/common/utils/getUserName';

import THandleContactChange from '../types/HandleContactChange';

type TArgs = {
  session: connect.ChatSession | null;
  taskId: string;
  handleContactChange: THandleContactChange;
  getSelectedTaskId: () => string | undefined;
  profile?: TCustomerProfile | null;
  icon: string;
  incomingChatNotificationsEnabledRef: React.MutableRefObject<boolean>;
  contactId: connect.Contact['contactId'];
};

const TYPING_INDICATOR_TIMEOUT_S = 15000;

const isConnectedContact = (contactId?: string) => {
  if (!contactId) {
    return false;
  }

  const contact = connectGetter(new connect.Agent(), 'getContacts')?.find((contact) => contact.contactId === contactId);

  return connectGetter(contact, 'getState')?.type === connect.ContactStateType.CONNECTED;
};

function getNotificationContent(data: connect.ChatEventData): string {
  if (data.Content) {
    return `${data.Content}`;
  }

  if (!data.Attachments?.length) {
    return 'You have a new message';
  }

  if (data.Attachments.length === 1) {
    return data.Attachments[0].AttachmentName || 'File received';
  }

  return 'Multiple files received';
}

export default function registerChatHandlers({
  session,
  taskId,
  contactId,
  handleContactChange,
  getSelectedTaskId,
  profile,
  icon,
  incomingChatNotificationsEnabledRef,
}: TArgs) {
  if (!session) {
    return;
  }

  session.onMessage((event) => {
    const { data } = event;

    const isTypingEvent = data.ContentType === CHAT_EVENTS.TYPING;
    const isEventType = data.Type === 'EVENT';

    if (isTypingEvent || !isConnectedContact(contactId)) {
      return;
    }

    if (data.ContentType === 'application/json') {
      const failedChat = JSON.parse(data.Content ?? '') as TFailedChat;
      return handleContactChange({
        taskId,
        newChat: {
          messageStatus: {
            status: MessageStatus.FAILED,
            ...failedChat,
          },
        },
      });
    }

    const isTaskDisplayed =
      window.location.pathname.includes(PAGE_ROUTES.WORKSPACE) ||
      window.location.pathname.includes(SOFTPHONE_PAGE_ROUTES.TASK);

    //if the task is not the currently selected or displayed one we have a new message
    const unreadMessage = !isEventType && (!isTaskDisplayed || getSelectedTaskId() !== taskId);

    if (
      incomingChatNotificationsEnabledRef.current &&
      unreadMessage &&
      'Notification' in window &&
      Notification.permission === 'granted' &&
      //ensure the message is not from the agent themselves
      data.ParticipantRole !== ParticipantRole.AGENT
    ) {
      const notification = new Notification(`New Message From ${getUserName({ ...profile, fallback: 'Customer' })}`, {
        body: getNotificationContent(data),
        icon,
      });
      notification.onclick = function () {
        window.focus();
      };
    }

    handleContactChange({
      taskId,
      newChat: {
        message: data,
        customerTyping: data.ParticipantRole === ParticipantRole.CUSTOMER ? false : void 0,
        unreadMessage,
        messageStatus:
          data.ParticipantRole !== ParticipantRole.CUSTOMER
            ? {
                messageId: data.Id,
                status: MessageStatus.SENT,
              }
            : void 0,
      },
    });
  });

  session.onTyping((event) => {
    const { data } = event;

    if (data.ParticipantRole !== ParticipantRole.CUSTOMER || !isConnectedContact(session.getChatDetails()?.contactId)) {
      return;
    }

    handleContactChange({ taskId, customerTyping: true });

    // Remove typing loader after 10 seconds in case customer stopped typing or erased the message
    const timeout = setTimeout(() => {
      handleContactChange({ taskId, customerTyping: false });
    }, TYPING_INDICATOR_TIMEOUT_S);

    return () => clearTimeout(timeout);
  });

  const handleParticipantPresenceEvent = (event: connect.ChatMessageEvent) => {
    const { data } = event;

    if (!isConnectedContact(session.getChatDetails()?.contactId)) {
      return;
    }

    handleContactChange({
      taskId,
      newChat: {
        message: data,
      },
    });
  };

  session.onParticipantIdle(handleParticipantPresenceEvent);
  session.onParticipantReturned(handleParticipantPresenceEvent);

  session.onDeliveredReceipt((event) => {
    const { data } = event;

    if (data.ContentType !== CHAT_EVENTS.MESSAGE_METADATA || !data.MessageMetadata || !isConnectedContact(contactId)) {
      return;
    }

    handleContactChange({
      taskId,
      newChat: {
        messageStatus: {
          messageId: data.MessageMetadata.MessageId,
          status: MessageStatus.DELIVERED,
        },
      },
    });
  });

  session.onReadReceipt((event) => {
    const { data } = event;

    if (data.ContentType !== CHAT_EVENTS.MESSAGE_METADATA || !data.MessageMetadata || !isConnectedContact(contactId)) {
      return;
    }

    handleContactChange({
      taskId,
      newChat: {
        messageStatus: {
          messageId: data.MessageMetadata.MessageId,
          status: MessageStatus.READ,
        },
      },
    });
  });
}
